import { useTranslation } from 'react-i18next'
import { Card, CardBody, Chip, Progress } from '@structural-climbing/ui'
import { MemberAvatar } from '../members/MemberAvatar'
import {
  classColor,
  trainerName,
  type CalendarEvent,
  type ClassColor,
  type ClassSession,
} from './types'
import { ageBand, timeOf } from './weekRange'

/** Tailwind needs the full class names in the source to keep them. */
export const COLOR_STYLES: Record<ClassColor, string> = {
  blue: 'border-l-blue-500 bg-blue-500/5',
  purple: 'border-l-violet-500 bg-violet-500/5',
  green: 'border-l-green-600 bg-green-600/5',
  orange: 'border-l-orange-500 bg-orange-500/5',
  red: 'border-l-red-600 bg-red-600/5',
  pink: 'border-l-pink-500 bg-pink-500/5',
  teal: 'border-l-teal-500 bg-teal-500/5',
}

export const COLOR_SWATCH: Record<ClassColor, string> = {
  blue: 'bg-blue-500',
  purple: 'bg-violet-500',
  green: 'bg-green-600',
  orange: 'bg-orange-500',
  red: 'bg-red-600',
  pink: 'bg-pink-500',
  teal: 'bg-teal-500',
}

export function ClassCard({ session, onPress }: { session: ClassSession; onPress: () => void }) {
  const { t } = useTranslation()
  const full = session.booked >= session.capacity
  const band = ageBand(session.min_age, session.max_age)
  const trainerLabel = session.trainer ? trainerName(session.trainer) : null

  return (
    <Card
      isPressable
      onPress={onPress}
      shadow="none"
      className={`w-full border border-l-4 border-divider ${COLOR_STYLES[classColor(session.color)]}`}
      aria-label={`${timeOf(session.starts_at)} ${session.title}`}
    >
      <CardBody className="gap-1.5 p-2.5">
        <p className="text-xs font-medium tabular-nums text-foreground/60">
          {timeOf(session.starts_at)}–{timeOf(session.ends_at)}
        </p>
        <p className="line-clamp-2 text-sm font-semibold leading-tight">{session.title}</p>
        <div className="flex flex-wrap items-center gap-1">
          <Chip size="sm" variant="flat" className="h-5 text-[11px]">
            {band ? t('calendar.ageBand', { band }) : t('calendar.allAges')}
          </Chip>
        </div>
        {session.trainer && trainerLabel && (
          <div className="flex min-w-0 items-center gap-1.5">
            <MemberAvatar
              userId={session.trainer.id}
              name={trainerLabel}
              avatarExt={session.trainer.avatar_ext}
              avatarUpdatedAt={session.trainer.avatar_updated_at}
              size="sm"
            />
            <p className="truncate text-xs text-foreground/70">{trainerLabel}</p>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Progress
            size="sm"
            aria-label={t('calendar.placesTaken')}
            value={session.booked}
            maxValue={session.capacity}
            color={full ? 'danger' : 'primary'}
            className="flex-1"
          />
          <p
            className={`text-xs tabular-nums ${full ? 'font-semibold text-danger' : 'text-foreground/60'}`}
          >
            {session.booked}/{session.capacity}
          </p>
        </div>
      </CardBody>
    </Card>
  )
}

export function EventCard({ event, onPress }: { event: CalendarEvent; onPress: () => void }) {
  const { t } = useTranslation()
  return (
    <Card
      isPressable
      onPress={onPress}
      shadow="none"
      className="w-full border border-dashed border-divider bg-content2/50"
    >
      <CardBody className="gap-1 p-2.5">
        <div className="flex items-center gap-1.5">
          <Chip size="sm" variant="flat" color="secondary" className="h-5 text-[11px]">
            {event.kind === 'competition'
              ? t('calendar.eventCompetition')
              : t('calendar.eventNews')}
          </Chip>
          <p className="text-xs tabular-nums text-foreground/60">{timeOf(event.starts_at)}</p>
        </div>
        <p className="line-clamp-2 text-sm font-medium leading-tight">{event.title}</p>
      </CardBody>
    </Card>
  )
}
